import { useTranslations as useNextIntlTranslations } from 'next-intl';
import { useSelector } from 'react-redux';
import { selectCurrentLanguage } from '@/app/store/slices/languageSlice';

/**
 * Custom hook that integrates next-intl with Redux language state
 * Provides translation functions with current language from Redux store
 */
export function useTranslation(namespace?: string): { t: any; currentLanguage: string } {
  // Get current language from Redux store
  const currentLanguage = useSelector(selectCurrentLanguage);
  
  // Get translations from next-intl
  const t = useNextIntlTranslations(namespace);
  
  return {
    t,
    currentLanguage,
  };
}

/**
 * Hook to get translations for a specific namespace
 * @param namespace - The translation namespace (e.g., 'common', 'auth', 'navigation')
 */
export function useNamespacedTranslation(namespace: string): { t: any; currentLanguage: string } {
  const currentLanguage = useSelector(selectCurrentLanguage);
  const t = useNextIntlTranslations(namespace);
  
  return {
    t,
    currentLanguage,
  };
}

/**
 * Hook to get common translations
 */
export function useCommonTranslation(): { t: any; currentLanguage: string } {
  return useNamespacedTranslation('common');
}

/**
 * Hook to get auth translations
 */
export function useAuthTranslation(): { t: any; currentLanguage: string } {
  return useNamespacedTranslation('auth');
}

/**
 * Hook to get navigation translations
 */
export function useNavigationTranslation(): { t: any; currentLanguage: string } {
  return useNamespacedTranslation('navigation');
}

/**
 * Hook to get error translations
 */
export function useErrorTranslation(): { t: any; currentLanguage: string } {
  return useNamespacedTranslation('errors');
}

/**
 * Hook to get validation translations
 */
export function useValidationTranslation(): { t: any; currentLanguage: string } {
  return useNamespacedTranslation('validation');
}

/**
 * Hook to get language translations
 */
export function useLanguageTranslation(): { t: any; currentLanguage: string } {
  return useNamespacedTranslation('language');
}

/**
 * Hook to get theme translations
 */
export function useThemeTranslation(): { t: any; currentLanguage: string } {
  return useNamespacedTranslation('theme');
}
